<template>
  <v-dialog 
    :value="value"
    max-width="750"
    persistent
    @click:outside="$emit('close')">
    <v-card
      flat
      tile>
      <v-toolbar flat>
        <v-toolbar-title>
          <v-card-title>
            Vendors on Promotion
          </v-card-title>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          text
          class="mr-2"
          :disabled="saving"
          @click="$emit('close')">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="disableSave"
          @click="saveClicked">
          Save
        </v-btn>
      </v-toolbar>
      <v-divider></v-divider>
      <v-data-table
        :items.sync="localParties"
        :height="tableHeight"
        :headers="headers"
        :loading="saving"
        dense
        item-key="party_id"
        fixed-header
        :footer-props="{'items-per-page-options': pageSizes}">
        <template v-slot:[`item.vendor_approved`]="{ item }">
          <v-simple-checkbox
            v-model="item.vendor_approved"
            color="primary"
            dense
            :disabled="getVendorApprovalDisabled(item) || allRelatedVendorsApproved"
            :ripple=false>
          </v-simple-checkbox>
        </template>
        <template v-slot:[`item.vendor_denied`]="{ item }">
          <v-simple-checkbox
            v-model="item.vendor_denied"
            color="primary"
            dense
            :disabled="getVendorDeniedDisabled(item)"
            :ripple=false>
          </v-simple-checkbox>
        </template>
        <template v-slot:[`item.audit.updated_on`]="{ item }">
          <span v-if="item.audit.updated_on">{{$config.formatTimestamp(item.audit.updated_on)}}</span>
          <span v-else>-</span>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog
      v-if="confirmDialog && changingVendorApprovalStatus"
      v-model="confirmDialog"
      width="400">
      <v-card flat>
        <v-card-title>Confirm Changes</v-card-title>
        <v-card-text>
          Are you sure that you would like adjust the vendor approval status for the selected vendor(s)?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-4 mb-2" @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn color="success" class="white--text mb-2" @click="confirmChanges()">
            Confirm
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
<script>
import PromoBillingParties from '@/axios/promotion-billing-party-endpoint'
import { fullWidthTable } from '@/mixins/table'
import { displayAlert } from '@/mixins/alert'
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
import { cloneDeep } from 'lodash'
export default {
  name: 'VendorApprovalModal',
  mixins: [fullWidthTable, displayAlert, userAccess, utils],
  props: {
    value: Boolean,
    readOnly: Boolean,
    promo: Object,
    promoBillingParties: Array
  },
  data () {
    return {
      localParties: [],
      confirmDialog: false,
      saving: false,
      from: 0,
      pageSizes: [25, 50, 100, 150, 200],
      pageSize: 200,
      filter: '',
      headers: [
        { text: '', value: '', sortable: false, class: 'accent white--text' },
        { text: 'Vendor Id', value: 'vendor_id', sortable: false, class: 'accent white--text' },
        { text: 'Name', align: 'start', sortable: false, value: 'name', class: 'accent white--text' },
        { text: 'Updated On', align: 'start', sortable: false, value: 'audit.updated_on', class: 'accent white--text' },
        { text: 'Updated By', align: 'start', sortable: false, value: 'audit.updated_by', class: 'accent white--text' },
        { text: 'Vendor Approved', align: 'center', sortable: false, value: 'vendor_approved', class: 'accent white--text' },
        { text: 'Vendor Denied', align: 'center', sortable: false, value: 'vendor_denied', class: 'accent white--text' },
      ],
    }
  },
  computed: {
    tableHeight () {
      return this.localParties.length <= 3 ? '220px' : '500px'
    },
    disableSave () {
      return this.saving || (this.adjustedParties.length === 0)
    },
    adjustedParties () {
      if (this.localParties?.length > 0) {
        return this.localParties.filter(party => {
          const promoParty = this.promoBillingParties.find(p => p.party_id === party.party_id)
          return promoParty.vendor_approved !== party.vendor_approved || promoParty.vendor_denied !== party.vendor_denied
        })
      }
      return []
    },
    changingVendorApprovalStatus () {
      return this.adjustedParties.some(ap => ap?.vendor_approved || ap?.vendor_denied)
    },
    relatedPromoVendors () {
      return this.promoBillingParties.filter(p => this.relatedVendorPartyIds.includes(p.party_id))
    },
    allRelatedVendorsApproved () {
      return this.limitAccessByRelatedVendors && this.relatedPromoVendors.every(rv => rv.vendor_approved)
    },
    categoryManagers () {
      return this.$store.getters.categoryManagers
    }
  },
  created () {
    this.localParties = cloneDeep(this.promoBillingParties)
  },
  watch: {
    promoBillingParties: {
      handler () {
        this.localParties = cloneDeep(this.promoBillingParties)
      },
      deep: true
    }
  },
  methods: {
    getVendorApprovalDisabled (item) {
      if (this.readOnly || this.saving || item.vendor_denied) return true
      if (this.limitAccessByRelatedVendors) {
        if (this.relatedVendorPartyIds.includes(item.party_id)) {
          const promoVendor = this.promoBillingParties.find(p => p.party_id === item.party_id)
          return promoVendor.vendor_approved === true
        }
        return true
      }
      return !(this.isAdmin || this.isARGAdmin)
    },
    getVendorDeniedDisabled (item) {
      if (this.readOnly || this.saving || item.vendor_approved) return true
      if (this.limitAccessByRelatedVendors) {
        if (this.relatedVendorPartyIds.includes(item.party_id)) {
          const promoVendor = this.promoBillingParties.find(p => p.party_id === item.party_id)
          return promoVendor.vendor_denied === true
        }
        return true
      }
      return false
    },
    saveClicked () {
      if (this.changingVendorApprovalStatus) this.confirmDialog = true
      else this.save()
    },
    confirmChanges () {
      this.confirmDialog = false
      this.save()
    },
    cancelChanges () {
      this.confirmDialog = false
    },
    constructPayload(party) {
      return {
        party_id: party.party_id,
        promo_id: this.promo.id,
        vendor_approved: party.vendor_approved,
        vendor_denied: party.vendor_denied,
        billing_override: false
      }
    },
    async save () {
      this.saving = true
      try {
        const updatePromises = this.adjustedParties.map(p =>  {
          return PromoBillingParties.put(this.promo.id, p.party_id, this.constructPayload(p), this.getVendorStatusParams(p))
        })
        const { rejected } = await this.getAllSettled(updatePromises, true)
        if (rejected.length > 0) throw rejected

        if (this.localParties.every(lp => lp.vendor_approved)) {
          const approvals = this.localParties.map(party => {
            const updateUser = this.adjustedParties.some(p => p.party_id === party.party_id)
              ? this.$auth.user.email
              : party.audit.updated_by
            return `${party.vendor_id} ${party.name}: ${updateUser}`
          }).join('\n')
          const notes = `Status automatically changed to Vendor Approved by system when all vendor approvals were granted. \nApproval details:\n${approvals}`
          this.$emit('autoSubmit', notes)
        }
        this.emitAlert(true, 'success', `Vendor parties have been successfully updated.`, [], true)
        this.$emit('updateHistory')
        this.$emit('updateBillingParties')
        this.$emit('close')
      } catch (e) {
        this.handleError(e)
      } finally {
        this.saving = false
      }
    },
    getVendorStatusParams(party) {
      if (this.limitAccessByRelatedVendors && (party.vendor_approved || party.vendor_denied)) {
        const categoryManager = this.categoryManagers.find(cm => cm.id === this.promo.category_manager_id)
        return { category_manager_email: categoryManager.email, vendor_id: party.vendor_id, vendor_name: party.name }
      }
      return null
    }
  }
}
</script>